import React, { useEffect, useMemo, useRef, useState } from "react";
import UseContext from "hooks/useContext";
import { ChevronDown } from "assets/icons";
import PerimeterMemberLabel from "pages/profil/perimeters/PerimeterMemberLabel";
import { sortMembersByName } from "pages/profil/perimeters/perimeterMembers";
import { getCountText } from "pages/profil/perimeters/perimeterText";

// Read-only: members are attached or detached from the edit modal.
const PerimeterMembersMenu = ({ members }) => {
    const { language } = UseContext();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const sortedMembers = useMemo(() => sortMembersByName(members), [members]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleMouseDown = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    return (
        <div ref={containerRef} className="relative w-48 shrink-0">
            <button
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
                className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 hover:bg-gray-50 focus:border-yellow-500 focus:outline-none"
            >
                <span className="truncate">
                    {getCountText(
                        language,
                        "perimeters.list.members",
                        members.length
                    )}
                </span>
                <span
                    aria-hidden="true"
                    className={`shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                >
                    <ChevronDown wh={16} color="#374151" />
                </span>
            </button>
            {isOpen && (
                <div className="absolute left-0 top-full z-30 mt-1 max-h-64 w-72 overflow-y-auto rounded-lg border border-gray-100 bg-white py-2 shadow-lg">
                    {sortedMembers.length === 0 ? (
                        <p className="px-4 py-2 text-sm text-gray-500">
                            {language.getValue("perimeters.list.noMembers")}
                        </p>
                    ) : (
                        <ul>
                            {sortedMembers.map((member) => (
                                <li key={member.id} className="px-4 py-2">
                                    <PerimeterMemberLabel member={member} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default PerimeterMembersMenu;
